import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button"
import {
  Dialog, DialogClose, 
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Calendar } from "../ui/calendar"
import { Checkbox } from "../ui/checkbox"
import { CalendarIcon } from "lucide-react"
import { format } from "date-fns"
import { cn } from "../../lib/utils"
import apiClient from '../../services/api';
import { Task, Goal, Category } from '../../types';
import { HierarchicalGoalSelect } from './HierarchicalGoalSelect';

interface TaskFormProps {
  task?: Task | null; // Optional: Pass existing task to edit
  goals: Goal[];
  categories: Category[]; // Add categories prop
  refreshTasks: () => void;
  // Add state setters for parent component to manage modal
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  clearTaskToEdit: () => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ 
  task, 
  goals, 
  categories, // Use categories
  refreshTasks, 
  isOpen, 
  setIsOpen, 
  clearTaskToEdit 
}) => {
  // Remove local isOpen state, use prop instead
  // const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalId, setGoalId] = useState<string | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined); // Add category state
  const [taskDate, setTaskDate] = useState<Date | undefined>(new Date()); 
  const [oneThing, setOneThing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!task;

  useEffect(() => {
    if (isOpen) {
      if (isEditing && task) {
        setTitle(task.title);
        setDescription(task.description || '');
        setGoalId(task.goal ? task.goal.toString() : undefined);
        setCategoryId(task.category ? task.category.toString() : undefined);
        setTaskDate(task.task_date ? new Date(task.task_date) : new Date());
        setOneThing(task.one_thing || false);
        setError(null);
      } else {
        // Reset form for new task
        setTitle('');
        setDescription('');
        setGoalId(undefined);
        setCategoryId(undefined);
        setTaskDate(new Date()); 
        setOneThing(false);
        setError(null);
      }
    } 
  }, [task, isEditing, isOpen]); // Depend on isOpen to reset/populate

  const handleClose = () => {
    setIsOpen(false);
    clearTaskToEdit(); // Clear editing state in parent
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const payload = {
      title,
      description,
      goal: (goalId && goalId !== "__none__") ? parseInt(goalId) : null,
      category: (categoryId && categoryId !== "__none__") ? parseInt(categoryId) : null,
      task_date: taskDate ? format(taskDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      one_thing: oneThing,
    };

    try {
      if (isEditing && task) {
        await apiClient.put(`/goals/tasks/${task.id}/`, payload);
      } else {
        await apiClient.post('/goals/tasks/', payload);
      }
      refreshTasks();
      handleClose(); // Close modal on success
    } catch (err: any) {
      console.error("Failed to save task:", err);
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save task.';
      if (errorData) {
        // Format DRF error messages
        errorMessage = Object.entries(errorData)
          .map(([key, value]) => `${key}: ${(value as string[]).join(', ')}`)
          .join(' \n');
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // Use isOpen and onOpenChange from props
    <Dialog open={isOpen} onOpenChange={(open: boolean) => {if (!open) handleClose(); else setIsOpen(open);}}>
      {/* DialogTrigger is now handled by parent component (Add/Edit buttons) */}
      {/* <DialogTrigger asChild> ... </DialogTrigger> */}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Task' : 'Add New Task'}</DialogTitle>
          <DialogDescription>
             {isEditing ? 'Update the details of your task.' : 'Create a new task.'} Click save when done.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Title */} 
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="task-title" className="text-right">Title</Label>
                <Input 
                    id="task-title" 
                    value={title} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                    className="col-span-3" 
                    required
                    disabled={isLoading}
                />
            </div>
             {/* Description */} 
             <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="task-description" className="text-right pt-2">Description</Label>
                <Textarea 
                    id="task-description" 
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    placeholder="(Optional) Add details..."
                    className="col-span-3"
                    disabled={isLoading}
                />
            </div>
             {/* Goal (Optional) - Replace Select */} 
             <div className="grid grid-cols-4 items-center gap-4">
               <Label htmlFor="task-goal" className="text-right">Goal (Optional)</Label>
               <HierarchicalGoalSelect
                   className="col-span-3"
                   goals={goals} // Pass all available goals
                   categories={categories} // Pass categories
                   value={goalId ? parseInt(goalId) : null}
                   onChange={(selectedId) => setGoalId(selectedId?.toString())}
                   placeholder="(Optional) Select associated goal"
                   allowClear={true}
                   disabled={isLoading}
                   filterActiveOnly={true}
               />
             </div>
             {/* Category (Optional) */}
             <div className="grid grid-cols-4 items-center gap-4">
               <Label htmlFor="task-category" className="text-right">Category</Label>
               {/* Use value={categoryId || ""} to handle undefined initial state and allow clearing */}
               {/* Add data-testid */}
               <Select data-testid="category-select" value={categoryId || ""} onValueChange={(value: string) => setCategoryId(value === "__none__" ? undefined : value)} disabled={isLoading}>
                 <SelectTrigger className="col-span-3">
                   <SelectValue placeholder="(Optional) Select category" />
                 </SelectTrigger>
                 <SelectContent>
                   {/* Use a non-empty string value for the None option */}
                   <SelectItem value="__none__">None</SelectItem>
                   {categories.map(c => (
                     <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                   ))}
                 </SelectContent>
               </Select>
             </div>
             {/* Task Date */} 
             <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="task-date" className="text-right">Date</Label>
                 <Popover>
                    <PopoverTrigger asChild>
                    <Button
                        variant={"outline"}
                        className={cn(
                        "col-span-3 justify-start text-left font-normal",
                        !taskDate && "text-muted-foreground"
                        )}
                        disabled={isLoading}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {taskDate ? format(taskDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                    <Calendar
                        mode="single"
                        selected={taskDate}
                        onSelect={setTaskDate}
                        initialFocus
                    />
                    </PopoverContent>
                </Popover>
            </div>
            {/* ONE THING Checkbox */} 
             <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="task-one-thing" className="text-right">ONE THING?</Label>
                <div className="col-span-3 flex items-center">
                  <Checkbox 
                    id="task-one-thing" 
                    checked={oneThing} 
                    onCheckedChange={(checked: boolean | 'indeterminate') => setOneThing(checked === true)} // Handle boolean state
                    disabled={isLoading}
                  />
                  <span className="ml-2 text-sm text-muted-foreground">Mark as the ONE THING for this day?</span>
                </div>
             </div>
             {/* Error Display */} 
             {error && (
                <div className="col-span-4 p-2 text-sm text-red-600 bg-red-100 border border-red-300 rounded">
                     {error}
                </div>
             )}
          </div>
          <DialogFooter>
             <div className="flex space-x-2 ml-auto"> 
                 <Button type="button" variant="secondary" onClick={handleClose} disabled={isLoading}>
                      Cancel
                 </Button>
                 <Button type="submit" disabled={isLoading}>
                     {isLoading ? 'Saving...' : (isEditing ? 'Update Task' : 'Save Task')}
                 </Button>
             </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskForm; 